import React, { useMemo, useState } from 'react';
import { PokemonCard, UserCard } from '../../types/pokemon';
import { Trophy, BarChart, Loader2, ChevronDown, ChevronUp, Percent, Package } from 'lucide-react';
import { usePackStats } from '../../hooks/usePackStats';
import { PackRecommendations } from './PackRecommendations';
import { useSetRarityStats } from '../../hooks/useSetRarityStats';
import { useSetCards } from '../../hooks/useSetCards';
import { usePackProbabilities } from '../../hooks/usePackProbabilities';
import { PackProbabilities } from './PackProbabilities';
import { useI18n } from '../../contexts/I18nContext';

interface SetStatisticsProps {
  cards: PokemonCard[];
  collection: Record<number, UserCard>;
  setCode?: string | null;
}

export function SetStatistics({ cards, collection, setCode }: SetStatisticsProps) {
  const { t } = useI18n();
  const { calculatePackRecommendations } = usePackStats(setCode || undefined);
  const { calculatePackProbabilities } = usePackProbabilities(setCode || undefined);
  const { stats: rarityStats, loading: statsLoading } = useSetRarityStats(setCode || undefined);
  const { cards: allSetCards } = useSetCards(setCode || undefined);
  const [isExpanded, setIsExpanded] = useState(false);

  // Calculate pack stats using all cards, not just filtered ones
  const rarityOrder: Record<string, number> = {
    '♛': 1,    // Couronne
    '☆☆☆': 2,  // 3 étoiles
    '☆☆': 3,   // 2 étoiles
    '☆': 4,    // 1 étoile
    '♢♢♢♢': 5, // 4 diamants
    '♢♢♢': 6,  // 3 diamants
    '♢♢': 7,   // 2 diamants
    '♢': 8     // 1 diamant
  };

  const sortedRarities = Object.entries(rarityStats || {})
    .sort(([a], [b]) => {
      const weightA = rarityOrder[a as keyof typeof rarityOrder] || 999;
      const weightB = rarityOrder[b as keyof typeof rarityOrder] || 999;
      return weightA - weightB;
    });

  const totalStats = Object.values(rarityStats).reduce(
    (acc, curr) => {
      if (!curr) return acc;
      const newAcc = {
        cards: acc.cards + curr.total,
        collected: acc.collected + curr.collected,
        percentage: 0
      };
      newAcc.percentage = newAcc.cards > 0 ? (newAcc.collected / newAcc.cards) * 100 : 0;
      return newAcc;
    },
    { cards: 0, collected: 0, percentage: 0 }
  );

  const packStats = useMemo(() => 
    calculatePackRecommendations(allSetCards, collection),
    [allSetCards, collection, calculatePackRecommendations]
  );

  const packProbabilities = useMemo(() => 
    calculatePackProbabilities(allSetCards, collection, { 
      showMissing: true, 
      showLessThanTwo: false, 
      excludeTradable: false 
    }),
    [allSetCards, collection, calculatePackProbabilities]
  );

  if (statsLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Statistiques de probabilités */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="p-4 sm:p-6">
         
          <PackProbabilities packStats={packProbabilities} />
        </div>
      </div>

      {/* Progression de la Collection */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        {/* En-tête avec progression et bouton d'expansion sur mobile */}
        <div 
          className="p-4 sm:p-6 flex items-center justify-between cursor-pointer lg:cursor-default"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Trophy className="w-5 h-5 text-indigo-600" />
              {t('set_detail.collection.progression')}
            </h2>
            <div className="mt-2">
              <div className="flex items-center justify-between text-sm text-gray-500">
                <span>{totalStats.collected} / {totalStats.cards} {t('set_detail.collection.cards')}</span>
                <span>{Math.round(totalStats.percentage)}%</span>
              </div>
              <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-indigo-600 rounded-full transition-all duration-300"
                  style={{ width: `${totalStats.percentage}%` }}
                />
              </div>
            </div>
          </div>
          <div className="lg:hidden ml-4">
            {isExpanded ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </div>
        </div>

        {/* Détails des statistiques */}
        <div className={`border-t border-gray-100 ${isExpanded ? 'block' : 'hidden'} lg:block`}>
          <div className="p-4 sm:p-6 space-y-4">
            {/* Distribution par rareté */}
            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
                <BarChart className="w-4 h-4 text-indigo-600" />
                {t('set_detail.collection.distribution_by_rarity')}
              </h3>
              <div className="space-y-2">
                {sortedRarities.map(([rarity, stats]) => (
                  <div key={rarity}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">{rarity}</span>
                      <span className="text-gray-900 font-medium">{stats.collected} / {stats.total}</span>
                    </div>
                    <div className="mt-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-indigo-600 rounded-full transition-all duration-300"
                        style={{ width: `${stats.percentage}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}